import { useState } from 'react';
import { cn } from '@/lib/utils';
import { renderLadder } from '../../../../shared/control-room-render/render-core.mjs';
import { Bib } from '../components/Bib';
import { ControlRoomStyles, CoreHtml } from '../components/ControlRoomCore';
import { member } from '../lib/memberCatalog';
import { LADDER, toCoreLadder } from '../lib/ladder';

const INK = 'oklch(0.16 0.005 95)';

/**
 * Public shop-window (E3) — the curated reveal of the Control Room, riding the
 * labs public/no-auth + email-capture scaffolding. Shows WHAT each helper does,
 * the OUTCOME, and the chain SHAPE ("N skills inside") — never the internals
 * (no prompts, SOPs, logic, ids or source paths — OQ6). Buyer-facing name:
 * "Control Room" (OQ7). Customer voice throughout.
 */

async function captureLead(payload: Record<string, unknown>) {
  // Same integration seam as the labs tools: best-effort POST to the leads
  // endpoint (Resend email + member match); UX completes even if unwired.
  try {
    await fetch('/api/labs/lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    /* no-op: lead capture is best-effort in v1 */
  }
}

function EmailCapture() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const valid = /.+@.+\..+/.test(email);
  if (sent) {
    return (
      <p className="rounded-[10px] border border-fd-teal bg-fd-teal-soft px-4 py-3 text-[14px] font-medium text-fd-text">
        You're on the list — we'll send you the Control Room walkthrough.
      </p>
    );
  }
  return (
    <form
      className="flex flex-col gap-2.5 sm:flex-row"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        void captureLead({ tool: 'control-room-shop-window', email });
        setSent(true);
      }}
    >
      <label className="flex-1">
        <span className="sr-only">Your email</span>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourbusiness.com"
          className="w-full rounded-[8px] border border-fd-border bg-fd-paper px-3.5 py-2.5 font-geist text-[14px] text-fd-text outline-none placeholder:text-fd-text-subtle focus:border-fd-text"
        />
      </label>
      <button
        type="submit"
        disabled={!valid}
        className={cn(
          'rounded-[8px] border-[1.5px] border-fd-text bg-fd-accent px-5 py-2.5 font-geist-mono text-[12px] font-semibold uppercase tracking-[0.06em] transition-transform duration-150 active:translate-y-px',
          !valid && 'opacity-60',
        )}
        style={{ color: INK, boxShadow: '2px 2px 0 0 var(--fd-text)' }}
      >
        Show me the Control Room
      </button>
    </form>
  );
}

export default function ControlRoomShopWindow() {
  const helpers = member?.helpers ?? [];
  const foundation = member?.foundation ?? null;

  return (
    <main className="mx-auto max-w-[880px] px-7 pb-20">
      <ControlRoomStyles />
      {/* ── hero ── */}
      <div className="pb-2 pt-[52px]">
        <div className="font-geist-mono text-[10.5px] uppercase tracking-[0.16em] text-fd-text-subtle">
          What you're actually buying
        </div>
        <h1 className="my-3 max-w-[640px] text-[34px] font-semibold leading-[1.06] tracking-[-0.03em] sm:text-[44px]">
          Your business gets a{' '}
          <span className="relative inline-block">
            Control Room
            <svg
              viewBox="0 0 320 14"
              preserveAspectRatio="none"
              className="absolute -bottom-3 left-[-2%] h-3.5 w-[104%] overflow-visible"
              aria-hidden
            >
              <path
                d="M4 9 C 70 3, 150 12, 210 6 S 300 5, 316 10"
                fill="none"
                stroke="var(--fd-coral)"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          </span>
        </h1>
        <p className="mt-5 max-w-[600px] text-[16.5px] leading-[1.55] text-fd-text-muted">
          A team of named helpers that know your offer, audience, proof and voice — each one proven on a
          real business before it reaches yours. You say the word; they do the work; you approve what ships.
        </p>
      </div>

      {/* ── the climb (B1 — the visual IS the promise) ── */}
      <div className="mb-3.5 mt-10 flex items-center gap-2.5">
        <span className="font-geist-mono text-[11px] uppercase tracking-[0.16em] text-fd-text-muted">
          Where you are — and where this takes you
        </span>
        <span className="h-px flex-1 bg-fd-border" />
      </div>
      <div className="overflow-x-auto pb-1">
        <CoreHtml
          html={renderLadder(
            toCoreLadder(LADDER, { foundationTag: 'the Foundation', climbTag: 'the Lab climbs', beforeTag: 'most coaches today' }),
          )}
          className="min-w-[640px] block"
        />
      </div>

      {/* ── helpers by job (curated: what + outcome only) ── */}
      <div className="mt-9 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        {helpers.map((group) => (
          <div key={group.label} className="relative overflow-hidden rounded-[12px] border border-fd-text bg-fd-paper p-5">
            <span className="absolute inset-x-0 top-0 h-[3px] bg-fd-accent" aria-hidden />
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-[16px] font-semibold tracking-[-0.01em]">{group.label}</span>
              <Bib>{group.skills.length}</Bib>
            </div>
            <ul className="space-y-2">
              {group.skills.map((s) => (
                <li key={s.id} className="text-[13px] leading-[1.5] text-fd-text-muted">
                  <span className="text-fd-teal">▸</span> {s.outcome}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* ── the 30-day install shape ── */}
      {foundation && (
        <>
          <div className="mb-3.5 mt-12 flex items-center gap-2.5">
            <span className="font-geist-mono text-[11px] uppercase tracking-[0.16em] text-fd-text-muted">
              How it gets installed — 30 days
            </span>
            <span className="h-px flex-1 bg-fd-border" />
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            {foundation.chains.map((c, i) => (
              <span key={c.id} className="flex items-center gap-2.5">
                <span className="flex items-center gap-2 rounded-[9px] border border-fd-border bg-fd-paper px-3 py-2">
                  <span className="font-geist-mono text-[9px] text-fd-text-subtle">{String(i + 1).padStart(2, '0')}</span>
                  <span className="text-[13px] font-medium">{c.name}</span>
                  <span className="font-geist-mono text-[9.5px] text-fd-teal">{c.skills.length} inside</span>
                </span>
                {i < foundation.chains.length - 1 && (
                  <span className="font-geist-mono text-[14px] font-bold text-fd-teal">→</span>
                )}
              </span>
            ))}
          </div>
          <p className="mt-4 max-w-[600px] text-[13.5px] leading-[1.55] text-fd-text-muted">
            Every step leaves something you keep in your own workspace — your brains, your sources, your
            reports. Nothing changes in your business without your approval.
          </p>
        </>
      )}

      {/* ── what the Control Room report covers (B4 — illustrative, honest) ── */}
      <div className="mb-3.5 mt-12 flex items-center gap-2.5">
        <span className="font-geist-mono text-[11px] uppercase tracking-[0.16em] text-fd-text-muted">
          What your Foundation Report covers
        </span>
        <span className="h-px flex-1 bg-fd-border" />
        <span className="font-geist-mono text-[10px] text-fd-text-subtle">generated from your own repo</span>
      </div>
      <div className="relative overflow-hidden rounded-[12px] border-[1.5px] border-fd-text bg-fd-paper shadow-[0_10px_30px_-22px_rgba(51,46,38,0.55)]">
        <span className="absolute inset-x-0 top-0 h-[4px] bg-fd-accent" aria-hidden />
        <div className="flex items-center gap-2.5 border-b border-fd-border px-5 py-3">
          <span
            className="grid h-6 w-6 place-items-center rounded-[5px] border-[1.5px] border-fd-text bg-fd-accent font-geist-mono text-[11px] font-semibold"
            style={{ color: INK, boxShadow: '1.5px 1.5px 0 0 var(--fd-text)' }}
          >
            R
          </span>
          <span className="font-geist-mono text-[11px] font-semibold uppercase tracking-[0.12em]">Foundation Report</span>
          <span className="ml-auto font-geist-mono text-[10px] text-fd-text-subtle">day 30 · your business, on one page</span>
        </div>
        <div className="grid grid-cols-1 divide-y divide-fd-border sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-4">
          {[
            { k: 'Installed', line: 'Your two brains, sensors, and first loop — what exists and where.' },
            { k: 'Pending approvals', line: 'Every proposed change waiting on your yes — nothing ships itself.' },
            { k: 'Source health', line: 'Which sources are fresh, which went quiet, what to capture next.' },
            { k: 'Loop status + next', line: 'What your first loop learned, and the evidence-backed next move.' },
          ].map((c) => (
            <div key={c.k} className="p-4">
              <div className="mb-1 font-geist-mono text-[9.5px] uppercase tracking-[0.12em] text-fd-accent-deep">{c.k}</div>
              <p className="text-[12px] leading-snug text-fd-text-muted">{c.line}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── email capture (the labs scaffolding) ── */}
      <div
        className="relative mt-12 overflow-hidden rounded-[14px] border-[1.5px] border-fd-text p-6 sm:p-7"
        style={{ background: 'linear-gradient(180deg, var(--fd-accent-soft), var(--fd-paper) 60%)' }}
      >
        <span className="absolute inset-x-0 top-0 h-[4px] bg-fd-accent" aria-hidden />
        <h2 className="text-[20px] font-semibold tracking-[-0.02em]">See your Control Room before you buy</h2>
        <p className="mb-4 mt-1.5 max-w-[520px] text-[14px] text-fd-text-muted">
          Drop your email and we'll send the full walkthrough — what gets installed, what you keep, and
          what it looks like running on a real business.
        </p>
        <EmailCapture />
        <p className="mt-3 font-geist-mono text-[10.5px] text-fd-text-subtle">
          No income promises. A founding build-along, proven on our own business first.
        </p>
      </div>
    </main>
  );
}
